import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";

// Motor de alertas real (Fase 3 do gpsdrive-backend, ver src/notifications/
// lá) — substitui o ConfiguredAlert que só existia em
// localStorage["wp-vehicle-alerts-{id}"]. Nome "alert-rules" (não
// "alerts") de propósito: já existe src/services/alerts.service.ts, um
// mock não relacionado ("Últimos alertas" via mockAlerts) — mesmo padrão
// de vehicle-permissions.service.ts: um service fino, sem lógica, só
// tipagem + chamada.
export type AlertType =
  | "ignition_on"
  | "ignition_off"
  | "overspeed"
  | "battery_tamper"
  | "long_stop"
  | "no_signal"
  | "off_hours"
  | "geofence"
  | "vibration"
  | "vehicle_blocked"
  | "vehicle_unblocked"
  | "anti_theft"
  | "displacement";

// Rótulo + emoji de cada AlertType — fonte única (extraído de
// ALERT_TYPE_OPTIONS em routes/index.tsx#TabAlertasEdit em 2026-09-04, pra
// reaproveitar também na tela Super Master > Notificações, que precisa dos
// mesmos tipos pra upload de som — ver notification-sounds.dto.ts no
// backend, que reaproveita a MESMA lista de ALERT_TYPES).
export const ALERT_TYPE_OPTIONS: { key: AlertType; label: string; emoji: string }[] = [
  { key: "ignition_on", label: "Ignição ligada", emoji: "🔑" },
  { key: "ignition_off", label: "Ignição desligada", emoji: "🚫" },
  { key: "overspeed", label: "Excesso de velocidade", emoji: "💨" },
  { key: "battery_tamper", label: "Bateria/energia violada", emoji: "🔋" },
  { key: "long_stop", label: "Parado por muito tempo", emoji: "⏱️" },
  // "Sem sinal" removido a pedido 2026-10-09 (motor não dispara mais).
  { key: "off_hours", label: "Condução fora do horário", emoji: "🌙" },
  { key: "geofence", label: "Cerca virtual", emoji: "🧭" },
  // Pedido 2026-09-08: mesmo atributo cru position.attributes.alarm ===
  // "vibration" que o Traccar já reporta (ver ALARM_LABELS em
  // routes/index.tsx) — vira uma regra de alerta configurável real no
  // AlertEngine (ver alert-engine.ts#evaluateCondition), não só um rótulo
  // de exibição.
  { key: "vibration", label: "Alerta de vibração", emoji: "📳" },
  { key: "vehicle_blocked", label: "Veículo bloqueado", emoji: "🔒" },
  { key: "vehicle_unblocked", label: "Veículo desbloqueado", emoji: "🔓" },
  { key: "anti_theft", label: "Antifurto (movimento com ignição desligada)", emoji: "🚨" },
  { key: "displacement", label: "Deslocamento acima do limite diário", emoji: "🛣️" },
];

export interface OffHoursConfig {
  weekdaysStart: string;
  weekdaysEnd: string;
  weekendEnabled: boolean;
  weekendStart: string;
  weekendEnd: string;
}

export interface AlertRuleConfig {
  maxSpeed?: number;
  stopMinutes?: number;
  noSignalMinutes?: number;
  maxDistanceKm?: number;
  offHours?: OffHoursConfig;
}

// Só usados quando type = "geofence" (drawer "Criar alerta para esta
// cerca" de Cercas Virtuais) — ver comentário em schema.prisma#AlertRule
// no backend.
export type TriggerWhen = "enter" | "exit" | "both";
export type ActionKind = "notify" | "block" | "unblock";

export interface AlertRule {
  id: string;
  traccarDeviceId: number;
  companyId: string;
  type: AlertType;
  name: string | null;
  geofenceId: number | null;
  triggerWhen: TriggerWhen | null;
  actionKind: ActionKind;
  config: AlertRuleConfig | null;
  channelApp: boolean;
  channelPush: boolean;
  channelEmail: boolean;
  dedupeMinutes: number;
  enabled: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAlertRuleInput {
  deviceId: number;
  type: AlertType;
  config?: AlertRuleConfig;
  channelApp: boolean;
  channelPush: boolean;
  dedupeMinutes: number;
}

export type UpdateAlertRuleInput = Partial<
  Pick<CreateAlertRuleInput, "type" | "config" | "channelApp" | "channelPush" | "dedupeMinutes"> & {
    enabled: boolean;
    // Faltava aqui mesmo já existindo em updateAlertRuleDto do backend
    // (notifications.dto.ts) — precisa pra Âncora poder trocar "somente
    // alertar"<->"alertar e bloquear" numa regra já criada sem recriá-la.
    actionKind: ActionKind;
  }
>;

// POST /notifications/rules/geofence-batch — uma regra por veículo
// selecionado, todas com a mesma cerca/nome/direção/ação. "channelEmail"
// já é salvo, mas o envio real de e-mail ainda não existe no backend (só
// App/Push por enquanto).
export interface CreateGeofenceAlertRuleBatchInput {
  geofenceId: number;
  deviceIds: number[];
  name: string;
  triggerWhen: TriggerWhen;
  actionKind: ActionKind;
  channelApp: boolean;
  channelEmail: boolean;
  dedupeMinutes: number;
}

export interface AlertRuleEvent {
  id: string;
  alertRuleId: string;
  traccarDeviceId: number;
  firedAt: string;
  message: string;
  read: boolean;
  // Tipo da regra que gerou o evento (join feito no backend, ver
  // notifications.repository.ts#listEvents) — usado pelo player de som de
  // notificação (use-notification-sound-player.ts) pra achar o áudio certo.
  type: AlertType;
}

// GET /notifications/events/page — histórico do veículo paginado e
// filtrado no servidor (aba Alertas de Editar dispositivo).
export interface AlertEventsPageParams {
  deviceId: number;
  page: number;
  pageSize: number;
  type?: string;
  since?: string;
  q?: string;
}

// Desde 2026-10-09 o histórico mescla os AlertEvents das regras com os
// eventos do próprio rastreador (ignição, alarmes, velocidade, cercas) —
// veículo sem regra nenhuma ficava com a lista vazia. Itens do rastreador
// vêm com source "tracker", alertRuleId null e, no alarme genérico, type
// "alarm" (fora de AlertType). trackerSince = início da janela deles.
export interface AlertEventsPageItem extends Omit<AlertRuleEvent, "alertRuleId" | "type"> {
  alertRuleId: string | null;
  type: AlertType | "alarm";
  source: "rule" | "tracker";
}

export interface AlertEventsPage {
  items: AlertEventsPageItem[];
  total: number;
  types: (AlertType | "alarm")[];
  trackerSince: string;
}

export type SimulatedAlertType =
  | "ignition_on"
  | "ignition_off"
  | "battery_tamper"
  | "vibration"
  | "anti_theft"
  | "vehicle_blocked"
  | "vehicle_unblocked";

export const alertRulesService = {
  // Só pro aparelho de quem pede, ver simulate-alert-panel.tsx.
  simulate: (deviceId: number, type: SimulatedAlertType, target: "me" | "owners" = "me") =>
    // `target` só vai quando é "owners": backend antigo recusa campo extra.
    gpsDrivePost<{ apps: number; browsers: number }>("/api/v1/notifications/simulate", target === "owners" ? { deviceId, type, target } : { deviceId, type }),

  listRules: (deviceId: number) => gpsDriveGet<AlertRule[]>(`/api/v1/notifications/rules?deviceId=${deviceId}`),

  createRule: (input: CreateAlertRuleInput) =>
    gpsDrivePost<AlertRule>("/api/v1/notifications/rules", input),

  createGeofenceRules: (input: CreateGeofenceAlertRuleBatchInput) =>
    gpsDrivePost<AlertRule[]>("/api/v1/notifications/rules/geofence-batch", input),

  // Todas as regras de geocerca da empresa, de uma vez — usado pela lista
  // de Cercas Virtuais (badge/filtro "Com bloqueio" + pré-preencher o
  // formulário ao reabrir), sem 1 chamada por cerca.
  ignitionSummary: () =>
    gpsDriveGet<{ traccarDeviceId: number; type: "ignition_on" | "ignition_off" }[]>(
      "/api/v1/notifications/rules/ignition-summary",
    ),
  listGeofenceRules: () => gpsDriveGet<AlertRule[]>("/api/v1/notifications/rules/geofence-summary"),

  updateRule: (id: string, patch: UpdateAlertRuleInput) =>
    gpsDrivePut<AlertRule>(`/api/v1/notifications/rules/${id}`, patch),

  deleteRule: (id: string) => gpsDriveDelete<void>(`/api/v1/notifications/rules/${id}`),

  // Botão "Dispensar alarme" da notificação nativa (alerta de vibração) —
  // hoje disparado 100% nativo (SnoozeAlarmReceiver.java no app Android,
  // sem passar pelo React), mas exposto aqui também pra uma eventual tela
  // web silenciar o mesmo alerta.
  snoozeRule: (id: string, minutes?: number) =>
    gpsDrivePut<AlertRule>(`/api/v1/notifications/rules/${id}/snooze`, minutes ? { minutes } : {}),

  // Sem deviceId: lista da frota inteira da empresa (usado pelo sino).
  listEvents: (deviceId?: number, limit = 50) =>
    gpsDriveGet<AlertRuleEvent[]>(
      `/api/v1/notifications/events?limit=${limit}${deviceId ? `&deviceId=${deviceId}` : ""}`,
    ),

  listEventsPage: (params: AlertEventsPageParams) => {
    const qs = new URLSearchParams({
      deviceId: String(params.deviceId),
      page: String(params.page),
      pageSize: String(params.pageSize),
    });
    if (params.type) qs.set("type", params.type);
    if (params.since) qs.set("since", params.since);
    if (params.q) qs.set("q", params.q);
    return gpsDriveGet<AlertEventsPage>(`/api/v1/notifications/events/page?${qs}`);
  },

  markEventRead: (id: string) => gpsDrivePut<void>(`/api/v1/notifications/events/${id}/read`),

  getVapidPublicKey: () =>
    gpsDriveGet<{ publicKey: string }>("/api/v1/notifications/push/vapid-public-key"),

  subscribePush: (subscription: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }) =>
    gpsDrivePost<void>("/api/v1/notifications/push/subscribe", subscription),

  unsubscribePush: (endpoint: string) =>
    gpsDriveDelete<void>("/api/v1/notifications/push/subscribe", { endpoint }),

  subscribeFcm: (payload: { token: string }) =>
    gpsDrivePost<void>("/api/v1/notifications/push/fcm-subscribe", payload),

  unsubscribeFcm: (token: string) =>
    gpsDriveDelete<void>("/api/v1/notifications/push/fcm-subscribe", { token }),

  // "Notificações" do app do cliente (ALERT_CATALOG#alertRuleType) — só
  // authGuard no backend, qualquer usuário da empresa liga/desliga o
  // próprio veículo, sem precisar da permissão granular "notifications"
  // (essa continua controlando as telas de admin). Ver
  // notifications.dto.ts#quickToggleAlertRuleDto.
  listQuickToggleRules: (deviceId: number) =>
    gpsDriveGet<AlertRule[]>(`/api/v1/notifications/rules/quick-toggle?deviceId=${deviceId}`),

  quickToggleRule: (payload: {
    deviceId: number;
    type: AlertType;
    enabled: boolean;
    config?: AlertRuleConfig;
    actionKind?: "notify" | "block";
  }) =>
    gpsDrivePut<AlertRule | null>("/api/v1/notifications/rules/quick-toggle", payload),
};
